import { Controller } from "@hotwired/stimulus"
import { triggerHaptic } from "../src/trigger_native_haptic"

// Routine Review: one screen at a time. A screen slides in, its sentence
// lands word by word (the routine check's two-tone lede), then what it's
// about rises row by row. Only the current screen's native bar is connected,
// by moving its data-bar-controller into data-controller, and it waits for
// the screen to land so a tap can't skip what it says. Each screen that
// enters is announced (routine-review:entered on the screen, with when it
// will have landed), so a screen's own animation can wait for it.
//
// A decision that changes the routine posts the finding's key and moves on
// once it lands. The reply lists the findings the routine still has, so a
// later screen the change settled drops out of the flow.
const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)"
const SLIDE_PX = 28
const OUT_MS = 180
const IN_MS = 360
const WORD_MS = 40
const RISE_GAP_MS = 70
const RISE_MS = 380
const BAR_DELAY_MS = 260

export default class extends Controller {
  static targets = ["step", "segment", "back", "planRow", "planEmpty"]
  static values = { fixUrl: String, focus: String, leaveUrl: String }

  connect() {
    this.index = 0
    this.applied = new Set()
    this.planned = new Set()
    this.dropped = new Set()
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.show(0, 1, { first: true })
  }

  disconnect() {
    clearTimeout(this.barTimer)
    clearTimeout(this.swapTimer)
    this.bars().forEach((bar) => bar.removeAttribute("data-controller"))
  }

  // --- Actions -----------------------------------------------------------

  primary() {
    if (this.busy) return

    const step = this.current
    if (this.isApplied(step)) return this.next()

    switch (step.dataset.primaryBehavior) {
      case "fix": return this.fix(step)
      case "plan": return this.addToPlan(step)
      case "done": return this.leave()
      default: return this.next()
    }
  }

  secondary() {
    if (this.busy) return
    this.next()
  }

  next() {
    const index = this.followingIndex(this.index, 1)
    if (index !== null) this.show(index, 1)
  }

  back() {
    const index = this.followingIndex(this.index, -1)
    if (index !== null) this.show(index, -1)
  }

  leave() {
    triggerHaptic()
    window.Turbo ? window.Turbo.visit(this.leaveUrlValue) : (window.location.href = this.leaveUrlValue)
  }

  // --- Decisions ---------------------------------------------------------

  async fix(step) {
    const keep = step.querySelector("[data-routine-review-keep]:checked")
    this.setBusy(step, true)

    try {
      const response = await fetch(this.fixUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ key: step.dataset.key, keep_id: keep?.value, focus: this.focusValue })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Couldn't update your routine")

      this.applied.add(step.dataset.key)
      this.dropSettled(data.keys || [])
      this.markApplied(step)
      triggerHaptic()
      this.setBusy(step, false)
      setTimeout(() => this.next(), this.reducedMotion ? 0 : 450)
    } catch (error) {
      this.setBusy(step, false)
      const message = step.querySelector('[data-routine-review-target~="error"]')
      if (message) {
        message.textContent = error.message || "Couldn't update your routine. Please try again."
        message.classList.remove("hidden")
      }
    }
  }

  addToPlan(step) {
    this.planned.add(step.dataset.key)
    this.markApplied(step)
    triggerHaptic("light")
    setTimeout(() => this.next(), this.reducedMotion ? 0 : 350)
  }

  // A finding the change settled (a later screen whose key the review no
  // longer has) leaves the flow, along with its progress segment.
  dropSettled(keys) {
    this.stepTargets.forEach((step, index) => {
      if (index <= this.index || !step.dataset.kind || !this.isFinding(step)) return
      if (keys.includes(step.dataset.key) || this.applied.has(step.dataset.key)) return

      this.dropped.add(step.dataset.key)
      this.segmentFor(step)?.classList.add("hidden")
    })
  }

  markApplied(step) {
    step.querySelector('[data-routine-review-target~="applied"]')?.classList.remove("hidden")
    step.querySelector('[data-routine-review-target~="error"]')?.classList.add("hidden")
    this.footers(step).forEach((footer) => footer.classList.toggle("hidden", footer.dataset.role !== "applied"))
    step.querySelectorAll("[data-routine-review-keep]").forEach((input) => (input.disabled = true))
    this.connectBar(step)
    this.syncPlan()
  }

  setBusy(step, busy) {
    this.busy = busy
    step.querySelectorAll('[data-routine-review-target~="pill"]').forEach((pill) => {
      pill.disabled = busy
      pill.setAttribute("aria-busy", busy.toString())
    })
    const bar = this.barFor(step)
    if (!bar) return

    const kind = bar.dataset.barController.replace("bridge--", "")
    if (kind === "single-action") {
      bar.setAttribute(`data-bridge--${kind}-loading-value`, busy.toString())
    } else {
      bar.setAttribute(`data-bridge--${kind}-disabled-value`, busy.toString())
    }
  }

  syncPlan() {
    let shown = 0
    this.planRowTargets.forEach((row) => {
      const on = this.applied.has(row.dataset.key) || this.planned.has(row.dataset.key)
      row.classList.toggle("hidden", !on)
      if (on) shown += 1
    })
    if (this.hasPlanEmptyTarget) this.planEmptyTarget.classList.toggle("hidden", shown > 0)
  }

  // --- Screens -----------------------------------------------------------

  show(index, direction, { first = false } = {}) {
    const from = this.current
    const to = this.stepTargets[index]
    this.index = index

    this.syncProgress()
    this.backTarget.classList.toggle("invisible", this.followingIndex(index, -1) === null)
    this.disconnectBars()

    const enter = () => {
      this.stepTargets.forEach((step) => step.classList.toggle("hidden", step !== to))
      if (!first) this.scrollToTop()
      this.enter(to, direction, first)
    }

    if (first || !from || from === to || this.reducedMotion) return enter()

    // Swapped on a timer rather than the animation's promise, so a paused
    // animation (a backgrounded web view) can't strand the flow.
    const leaving = from.animate(
      [{ opacity: 1, transform: "translateX(0)" }, { opacity: 0, transform: `translateX(${-SLIDE_PX * direction}px)` }],
      { duration: OUT_MS, easing: "ease-in", fill: "forwards" }
    )
    clearTimeout(this.swapTimer)
    this.swapTimer = setTimeout(() => {
      leaving.cancel()
      enter()
    }, OUT_MS)
    triggerHaptic("light")
  }

  enter(step, direction, first) {
    this.footers(step).forEach((footer) => {
      footer.classList.toggle("hidden", (footer.dataset.role === "applied") !== this.isApplied(step))
    })

    if (this.reducedMotion) {
      this.announce(step, 0)
      return this.connectBar(step)
    }

    const words = this.words(step)
    const rises = [...step.querySelectorAll("[data-routine-review-rise]")]
    const delay = first ? 120 : 0

    if (!first) {
      step.animate(
        [{ opacity: 0, transform: `translateX(${SLIDE_PX * direction}px)` }, { opacity: 1, transform: "translateX(0)" }],
        { duration: IN_MS, easing: EASE }
      )
    }
    words.forEach((word, i) => {
      word.animate(
        [{ opacity: 0, filter: "blur(3px)" }, { opacity: 1, filter: "blur(0)" }],
        { duration: 320, delay: delay + i * WORD_MS, easing: "ease-out", fill: "backwards" }
      )
    })
    const landed = delay + words.length * WORD_MS
    rises.forEach((element, i) => {
      element.animate(
        [{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: RISE_MS, delay: landed + i * RISE_GAP_MS, easing: EASE, fill: "backwards" }
      )
    })
    this.announce(step, landed + Math.max(rises.length - 1, 0) * RISE_GAP_MS + RISE_MS)

    clearTimeout(this.barTimer)
    this.barTimer = setTimeout(() => this.connectBar(step), Math.min(landed, 700) + BAR_DELAY_MS)
  }

  announce(step, settled) {
    this.dispatch("entered", { target: step, detail: { settled } })
  }

  // The lede's words, each in a span of its own so they can land one by
  // one. Split once; the text reads the same either way.
  words(step) {
    const lede = step.querySelector("[data-routine-review-lede]")
    if (!lede) return []

    if (!lede.dataset.split) {
      lede.querySelectorAll(":scope > span").forEach((part) => {
        const text = part.textContent.trim()
        part.textContent = ""
        text.split(/\s+/).forEach((word, i, all) => {
          const span = document.createElement("span")
          span.className = "inline-block"
          span.textContent = word
          part.append(span)
          if (i < all.length - 1) part.append(" ")
        })
        part.after(" ")
      })
      lede.dataset.split = "true"
    }
    return [...lede.querySelectorAll(":scope > span > span")]
  }

  syncProgress() {
    const visible = this.stepTargets.filter((step) => !this.dropped.has(step.dataset.key))
    const position = visible.indexOf(this.current)
    visible.forEach((step, i) => {
      const fill = this.segmentFor(step)?.firstElementChild
      if (fill) fill.style.transform = `scaleX(${i <= position ? 1 : 0})`
    })
  }

  // The nearest scrolling ancestor: the web drawer's body, or the page in
  // the native modal.
  scrollToTop() {
    let scroller = this.element.parentElement
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement
    ;(scroller || document.scrollingElement)?.scrollTo({ top: 0 })
  }

  // --- Native bars -------------------------------------------------------

  // Disconnect first, then connect on the next frame, so native never sees
  // two bars at once or the old one's disconnect after the new connect.
  connectBar(step) {
    if (step !== this.current) return

    this.disconnectBars()
    const bar = this.barFor(step)
    if (!bar) return

    requestAnimationFrame(() => {
      if (step === this.current) bar.setAttribute("data-controller", bar.dataset.barController)
    })
  }

  disconnectBars() {
    clearTimeout(this.barTimer)
    this.bars().forEach((bar) => bar.removeAttribute("data-controller"))
  }

  barFor(step) {
    const role = this.isApplied(step) ? "applied" : "main"
    return [...step.querySelectorAll('[data-routine-review-target~="bar"]')].find((bar) => bar.dataset.role === role)
  }

  bars() {
    return [...this.element.querySelectorAll('[data-routine-review-target~="bar"]')]
  }

  footers(step) {
    return [...step.querySelectorAll('[data-routine-review-target~="footer"]')]
  }

  // --- State -------------------------------------------------------------

  get current() {
    return this.stepTargets[this.index]
  }

  isFinding(step) {
    return !["intro", "working", "plan"].includes(step.dataset.kind)
  }

  isApplied(step) {
    return this.applied.has(step.dataset.key) || this.planned.has(step.dataset.key)
  }

  // The next screen in `direction` that is still in the flow, or null.
  followingIndex(index, direction) {
    for (let i = index + direction; i >= 0 && i < this.stepTargets.length; i += direction) {
      if (!this.dropped.has(this.stepTargets[i].dataset.key)) return i
    }
    return null
  }

  segmentFor(step) {
    return this.segmentTargets.find((segment) => segment.dataset.key === step.dataset.key)
  }
}
